// pipe (list findability): the VIEW-SAFE half of the shared widening — the query-box wiring, the
// page/found row split, and the one honest status line every ERP list screen renders.
//
// Import discipline (load-bearing, mirrors erp-table-controls.tsx / transition-affordance.tsx):
// ONLY @affine/i18n, React, the pure engine types and its css module. NO runtime ErpService /
// workbench / DI / Electron — the pure `*-view.tsx` seams import this, and they must stay
// happy-dom renderable. The CONTAINER side (which does hold ErpService) is use-erp-widen.ts.
import { useI18n } from '@affine/i18n';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';

import {
  type ErpWiden,
  IDLE_WIDEN,
  WIDEN_MIN_LENGTH,
  widenExtras,
  type WidenState,
} from './erp-list-widen';
import * as styles from './erp-widen.css';

/**
 * The per-page filter box's state, plus the server-side widen it drives.
 *
 * Replaces the three lines every ERP view had copied (`useState(initialQuery)` +
 * a re-seed effect + `useDeferredValue`) and adds the widen call, so a screen can never wire the
 * box to the client filter and forget the server one. The deep-link case comes free: a `?q=`
 * landing (a scan / global-search result) now widens on arrival instead of filtering an
 * arbitrary page.
 */
export function useWidenQuery(
  initialQuery: string | undefined,
  // Only the keystroke sink is needed here — accepting the narrow shape keeps this usable by a
  // screen whose rows are a RecordRow SUBTYPE (Customers' AccountRow) without a cast.
  widen?: Pick<ErpWiden<{ id: string }>, 'onQuery'>
) {
  const [query, setQuery] = useState(initialQuery ?? '');
  // Re-seed when a NEW deep-link arrives while this view is already mounted.
  useEffect(() => setQuery(initialQuery ?? ''), [initialQuery]);
  const onQuery = widen?.onQuery;
  useEffect(() => {
    onQuery?.(query);
  }, [query, onQuery]);
  // Keystrokes update the input urgently; the table recompute rides the deferred value, so typing
  // is never blocked on a full filter+sort+re-render of the table subtree (standing perf rule).
  const deferredQuery = useDeferredValue(query);
  return { query, setQuery, deferredQuery };
}

export interface WidenRows<T extends { id: string }> {
  /** What to render: the screen's own filtered+sorted page rows, then the widened extras. */
  rows: T[];
  /** Which of `rows` came from the server-side search (never on the loaded page). */
  foundIds: ReadonlySet<string>;
  /** How many of `rows` came from the loaded page — the honest half of the status line. */
  pageCount: number;
  /** How many came from the search. */
  foundCount: number;
}

const NO_FOUND: ReadonlySet<string> = new Set<string>();

/**
 * Append the widened hits BELOW the screen's own displayed rows, deduped by id.
 *
 * The extras are deliberately NOT run through the client-side filter/sort: the operator typed a
 * term, the SERVER matched these rows against it, and re-hiding them behind a substring test over
 * the columns this grid happens to render is the very dead end this slice exists to close (the
 * same reasoning as the Records deep-link pin — a row the operator asked for by name is never
 * filtered away by client view state).
 */
export function useWidenRows<T extends { id: string }>(
  displayed: readonly T[],
  widen?: ErpWiden<T>
): WidenRows<T> {
  const hits = widen?.hits;
  return useMemo(() => {
    const extras = hits ? widenExtras(displayed, hits) : [];
    if (extras.length === 0) {
      return {
        rows: displayed as T[],
        foundIds: NO_FOUND,
        pageCount: displayed.length,
        foundCount: 0,
      };
    }
    return {
      rows: [...displayed, ...extras],
      foundIds: new Set(extras.map(r => r.id)),
      pageCount: displayed.length,
      foundCount: extras.length,
    };
  }, [displayed, hits]);
}

export interface ErpWidenStatusProps {
  /** Absent = this screen has no widen wired (renders nothing). */
  widen?: Pick<ErpWiden<{ id: string }>, 'state'>;
  /** The term currently applied (the deferred one — what the counts describe). */
  query: string;
  pageCount: number;
  foundCount: number;
  /** True when the screen is rendering NO rows at all — the status then carries the whole answer
   *  (and must never be the confident "no such record" the old client-only panel gave). */
  empty?: boolean;
}

/**
 * The one honest statement a widened list makes about itself. Renders nothing when there is
 * nothing honest to add (no term, no widen, no extras) so quiet screens stay quiet.
 *
 * Every distinct outcome is its own sentence — the no-silent-partial contract the reports/export
 * surfaces already hold: still searching ≠ found nothing; the search FAILED ≠ the record does not
 * exist; and a result at the server ceiling says so instead of pretending to be the whole set.
 */
export function ErpWidenStatus({
  widen,
  query,
  pageCount,
  foundCount,
  empty,
}: ErpWidenStatusProps) {
  const t = useI18n();
  const state: WidenState = widen?.state ?? IDLE_WIDEN;
  const term = query.trim();
  const lines: string[] = [];
  if (state.searching) {
    lines.push(t['com.affine.pipe.widen.searching']());
  } else if (state.failed) {
    lines.push(t['com.affine.pipe.widen.failed']());
  } else if (foundCount > 0) {
    lines.push(
      t['com.affine.pipe.widen.mixed']({
        page: String(pageCount),
        count: foundCount,
      })
    );
  } else if (widen && term !== '' && empty) {
    if (state.tooShort) {
      // NOTHING was asked of the server (the term is under the engine's minimum), so the only
      // honest statement is what the operator must do to get an answer — never "searched every
      // record". `WIDEN_MIN_LENGTH` is the SSOT: this status renders only for list screens, which
      // all run the engine's default minimum.
      lines.push(
        t['com.affine.pipe.widen.too-short']({
          min: String(WIDEN_MIN_LENGTH),
        })
      );
    } else if (state.filteredOut) {
      // The search DID find the term — this screen's own narrowing (imported history on the live
      // work queue, another job on a job-scoped grid) is what excluded it. Point at that instead
      // of denying the record exists.
      lines.push(t['com.affine.pipe.widen.elsewhere']({ query: term }));
    } else if (!state.capped) {
      // Searched the WHOLE list (not just this page) and there is genuinely nothing — the only
      // state in which a "nothing matches" claim is true. A CAPPED result is explicitly excluded:
      // the server stopped at its ceiling, so rows this screen would accept may well exist behind
      // it (a per-screen `accept` can drop every returned hit and still leave the set non-empty) —
      // the capped line below then carries the whole, truthful answer.
      lines.push(t['com.affine.pipe.widen.no-matches']({ query: term }));
    }
  }
  if (state.capped) lines.push(t['com.affine.pipe.widen.capped']());
  if (lines.length === 0) return null;
  return (
    <div
      className={
        state.failed ? `${styles.status} ${styles.statusFailed}` : styles.status
      }
      role={state.failed ? 'alert' : 'status'}
      aria-live="polite"
      data-testid="erp-widen-status"
    >
      {lines.map(line => (
        <span key={line}>{line}</span>
      ))}
    </div>
  );
}

/** The server's page-cap banner: this list is the newest `count` rows and there are more. One
 *  presenter for every screen so the wording (and the "use the filter box, it searches everything"
 *  next step) is stated once — the same no-silent-partial contract as the export/scan headers. */
export function ErpPageTruncated({
  truncated,
  count,
}: {
  truncated?: boolean;
  count: number;
}) {
  const t = useI18n();
  if (!truncated) return null;
  return (
    <div
      className={styles.status}
      role="status"
      data-testid="erp-page-truncated"
    >
      {t['com.affine.pipe.table.page-truncated']({ count })}
    </div>
  );
}

/** The non-colour cue on a widened row (a colour-only distinction is not an accessible one).
 *  Rendered inside the row's FIRST cell by every grid — one badge, one label. */
export function WidenedBadge({ near = false }: { near?: boolean }) {
  const t = useI18n();
  return (
    <span className={styles.foundBadge}>
      {near
        ? t['com.affine.pipe.search.near']()
        : t['com.affine.pipe.widen.found-badge']()}
    </span>
  );
}

/** The row attributes for a widened row: the shared accent style + a stable test/automation hook.
 *  Callers spread this onto their `<TableBodyRow>` so the marking is defined once. */
export function widenedRowProps(found: boolean) {
  return found
    ? { className: styles.foundRow, 'data-pipe-widened': 'true' }
    : {};
}
